/*
Purpose: Build & download a student's full profile (mirrors the /student-profile page) as a PDF.
Project: TCMS
*/

import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Moment from 'moment';
import { options_countries } from '../global';

/**
 * @typedef {Object} StudentProfilePdfInput
 * @property {Object} studentData          Single student record (getSingleStudent response row).
 * @property {Object} secondaryParentData  Additional parent/guardian record (may be empty).
 * @property {number} studentAge           Pre-computed age in years.
 * @property {Array<{question:string, answer:string}>} fieldDetails  Centre's custom enrolment fields.
 */

/**
 * Generates the student profile PDF and triggers a browser download.
 * @param {StudentProfilePdfInput} input
 */
export function downloadStudentProfilePdf({ studentData, secondaryParentData, studentAge, fieldDetails }) {

    const StudentData = studentData || {};
    const StudentSecParData = secondaryParentData || {};
    const StudentAge = studentAge;

    const doc = new jsPDF({ format: 'a4', orientation: 'portrait' });

    // Helper to fall back to N/A for empty values
    const val = (v) => (v !== null && v !== undefined && String(v).trim() !== '') ? String(v) : 'N/A';

    // Map a stored numeric country code to its readable name
    const countryName = (code) => {
        if (code === null || code === undefined || String(code).trim() === '') return 'N/A';
        const found = options_countries.find(o => String(o.value) === String(code));
        return found ? found.label : String(code);
    };

    // Map diagnostics test-duration code to its label
    const durationLabel = (d) => {
        if (d === null || d === undefined || String(d).trim() === '') return 'N/A';
        const map = { '15': '15 minutes', '30': '30 minutes', '45': '45 minutes', '1': '1 hour' };
        return map[String(d)] || String(d);
    };

    const center_name = sessionStorage.getItem('center_name') || 'Student';
    const studentName = StudentData && StudentData.st_first_name != null && StudentData.st_first_name != ''
        ? `${StudentData.st_first_name} ${StudentData.st_surname || ''}`.trim()
        : 'N/A';

    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    doc.setFontSize(14);
    doc.text(center_name, 14, 14);
    doc.setFontSize(12);
    const heading = 'Student Profile';
    doc.text(heading, pageWidth - 14 - doc.getTextWidth(heading), 14);

    doc.setFontSize(11);
    doc.text(studentName, 14, 22);

    // Renders a labelled section with a two-column (label/value) table.
    // autoTable handles page breaks automatically, so multi-page output just works.
    let nextY = 28;
    const renderSection = (title, rows) => {
        if (!rows || rows.length === 0) return;
        doc.autoTable({
            head: [[{ content: title, colSpan: 2 }]],
            body: rows,
            startY: nextY,
            styles: { fontSize: 10, valign: 'top', cellPadding: 2 },
            headStyles: { fillColor: [100, 100, 100], textColor: 255, fontSize: 11, halign: 'start' },
            columnStyles: {
                0: { cellWidth: 60, fontStyle: 'bold' },
                1: { cellWidth: 'auto' },
            },
        });
        nextY = doc.lastAutoTable.finalY + 6;
    };

    const isAdult = StudentData && StudentData.st_is_adultlearner != null && StudentData.st_is_adultlearner == 1;

    // 1. Student Particulars
    renderSection('Student Particulars', [
        ['First Name', val(StudentData ? StudentData.st_first_name : null)],
        ['Last Name', val(StudentData ? StudentData.st_surname : null)],
        ['Nickname', val(StudentData ? StudentData.st_nickname : null)],
        ['Gender', val(StudentData ? StudentData.st_gender : null)],
        ['Date of Birth', StudentData && StudentData.st_dob != '' && StudentData.st_dob != null ? Moment(StudentData.st_dob).format('DD-MM-YYYY') : 'N/A'],
        ['Age', StudentAge ? `${StudentAge} years` : 'N/A'],
        ['Year Level', val(StudentData ? StudentData.level_name : null)],
        ['School', val(StudentData ? StudentData.fk_sc_id : null)],
        ['Learner Type', isAdult ? 'Adult Learner' : 'Student'],
        ['Email', val(StudentData ? StudentData.st_email : null)],
        ['Contact Number', val(StudentData ? StudentData.st_phone : null)],
        ['Address', val(StudentData ? StudentData.st_address : null)],
        ['City/Town', val(StudentData ? StudentData.st_city : null)],
        ['County/Region/State', val(StudentData ? StudentData.st_state : null)],
        ['Country', countryName(StudentData ? StudentData.st_country : null)],
        ['Postal Code', val(StudentData ? StudentData.st_zip : null)],
        ['Timezone', val(StudentData ? StudentData.st_timezone : null)],
        ['Illnesses / Disabilities', val(StudentData ? StudentData.st_illnesses : null)],
    ]);

    // 2. Student Secret Question & Answer
    renderSection('Student Secret Question & Answer', [
        ['Secret Question', val(StudentData ? StudentData.shs_question : null)],
        ['Secret Answer', val(StudentData ? StudentData.shs_answer : null)],
    ]);

    // 3 & 4. Parents / Guardians + Contact Details (skipped for adult learners)
    if (!isAdult) {
        renderSection('Parents / Guardians Details', [
            ['Relationship', val(StudentData ? StudentData.ps_relationship : null)],
            ['Name', StudentData && StudentData.par_firstname != '' && StudentData.par_firstname != null ? `${StudentData.par_firstname} ${StudentData.par_surname || ''}`.trim() : 'N/A'],
            ['Email', val(StudentData ? StudentData.par_emailid : null)],
            ['Contact Number', val(StudentData ? StudentData.par_phone : null)],
            ['Address', val(StudentData ? StudentData.par_address : null)],
            ['City/Town', val(StudentData ? StudentData.par_city : null)],
            ['County/Region/State', val(StudentData ? StudentData.par_state : null)],
            ['Country', countryName(StudentData ? StudentData.par_country : null)],
            ['Postal Code', val(StudentData ? StudentData.par_zip : null)],
        ]);

        // Parent secret Q&A
        renderSection('Parents / Guardians Secret Question & Answer', [
            ['Secret Question', val(StudentData ? StudentData.parent_questions : null)],
            ['Secret Answer', val(StudentData ? StudentData.parent_answer : null)],
        ]);

        // Additional Parent / Guardian (when present)
        if (StudentSecParData && StudentSecParData.pk_par_key != null && StudentSecParData.pk_par_key != '') {
            renderSection('Additional Parents / Guardians', [
                ['Name', StudentSecParData.par_firstname != '' && StudentSecParData.par_firstname != null ? `${StudentSecParData.par_firstname} ${StudentSecParData.par_surname || ''}`.trim() : 'N/A'],
                ['Relationship', val(StudentSecParData.ps_relationship)],
                ['Email', val(StudentSecParData.par_emailid)],
                ['Contact Number', val(StudentSecParData.par_phone)],
            ]);
        }
    }

    // 5. Lead Details
    renderSection('Lead Details', [
        ['Preferred Method Of Contact', val(StudentData ? StudentData.lead_pref_meth_contact : null)],
        ['Preferred Time To Contact', val(StudentData ? StudentData.lead_pref_tim_contact : null)],
        ['Referral Source', val(StudentData ? StudentData.lead_ref_source : null)],
        ['Campaign', val(StudentData ? StudentData.lead_ref_campaign : null)],
        ['Status Date', StudentData && StudentData.ls_date != '' && StudentData.ls_date != null ? Moment(StudentData.ls_date).format('DD-MM-YYYY') : 'N/A'],
        ['Status', val(StudentData ? StudentData.ls_status : null)],
        ['Status Description', val(StudentData ? StudentData.ls_description : null)],
    ]);

    // 6. Initial Diagnostics Details + Scores
    renderSection('Initial Diagnostics Details', [
        ['Type', val(StudentData ? StudentData.la_type : null)],
        ['Date', StudentData && StudentData.la_date != '' && StudentData.la_date != null ? Moment(StudentData.la_date).format('DD-MM-YYYY') : 'N/A'],
        ['Assessor Name', val(StudentData ? StudentData.la_assessor : null)],
        ['Test Duration', durationLabel(StudentData ? StudentData.la_duration : null)],
        ['Result Outcome', val(StudentData ? StudentData.la_outcome : null)],
        ['Goal', val(StudentData ? StudentData.la_goals : null)],
        ['Notes', val(StudentData ? StudentData.la_notes : null)],
        ['Reading Age', StudentData && StudentData.la_reading_age != '' && StudentData.la_reading_age != null ? `${StudentData.la_reading_age} years` : 'N/A'],
        ['Spelling Year Level', val(StudentData ? StudentData.lable_spell_year : null)],
        ['Maths Year Level', val(StudentData ? StudentData.lable_maths_year : null)],
        ['Comprehension Age', StudentData && StudentData.la_comprehension_age != '' && StudentData.la_comprehension_age != null ? `${StudentData.la_comprehension_age} years` : 'N/A'],
        ['English Year Level', val(StudentData ? StudentData.la_english_year_level : null)],
    ]);

    // 7. Other Details (dynamic enrolment fields the centre defined in enrolment settings)
    if (fieldDetails && fieldDetails.length > 0) {
        renderSection('Other Details', fieldDetails.map(field => [
            val(field.question),
            val(field.answer),
        ]));
    }

    // Build a safe file name: <student_name>_profile_<timestamp>.pdf
    let formattedName = studentName.replace(/['"]/g, "").replace(/\s+/g, "_").toLowerCase();
    let now = new Date();
    let timestamp = now.getFullYear() +
        ("0" + (now.getMonth() + 1)).slice(-2) +
        ("0" + now.getDate()).slice(-2) + "_" +
        ("0" + now.getHours()).slice(-2) +
        ("0" + now.getMinutes()).slice(-2) +
        ("0" + now.getSeconds()).slice(-2);

    doc.save(`${formattedName}_profile_${timestamp}.pdf`);
}
